import { z } from "zod";

export const setRoundGroupSchema = z.object({
  roundId: z.string().min(1),
  expectedGroupId: z.string().nullable(),
  selection: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("none") }),
    z.object({ kind: z.literal("existing"), groupId: z.string().min(1) }),
    z.object({
      kind: z.literal("new"),
      name: z.string().trim().min(1).max(100),
    }),
  ]),
});

export type RoundGroup = {
  id: string;
  name: string;
  rounds: { id: string; name: string; archivedAt: Date | string | null }[];
};

/** Group only the supplied visible rounds; membership never adds new rows. */
export function groupVisibleRounds<
  T extends { round: { id: string; name: string } },
>(
  entries: T[],
  groups: RoundGroup[],
): { id: string; name: string | null; entries: T[] }[] {
  const groupByRound = new Map(
    groups.flatMap((group) =>
      group.rounds.map((round) => [round.id, group] as const),
    ),
  );
  const sections = new Map<
    string,
    { id: string; name: string | null; entries: T[] }
  >();

  for (const entry of entries) {
    const group = groupByRound.get(entry.round.id);
    const id = group ? `group:${group.id}` : `round:${entry.round.id}`;
    const section = sections.get(id);

    if (section) {
      section.entries.push(entry);
    } else {
      sections.set(id, { id, name: group?.name ?? null, entries: [entry] });
    }
  }

  return [...sections.values()].map((section) => ({
    ...section,
    entries:
      section.name == null
        ? section.entries
        : section.entries.sort((a, b) =>
            a.round.name.localeCompare(b.round.name),
          ),
  }));
}
